import {
    SHOP_VERTICAL_TEMPLATE_DESCRIPTORS,
    type ShopVerticalPresentationProfile,
    type ShopVerticalTemplateDescriptor,
} from "./shopVerticalCatalog";

function descriptorPresentationProfile(
    descriptor: ShopVerticalTemplateDescriptor,
): ShopVerticalPresentationProfile {
    return descriptor.presentationProfile ?? descriptor.inspiration;
}

const presentationFamilyByTemplateCode = new Map<string, ShopVerticalPresentationProfile>(
    SHOP_VERTICAL_TEMPLATE_DESCRIPTORS.map((descriptor) => [
        descriptor.code,
        descriptorPresentationProfile(descriptor),
    ]),
);

if (presentationFamilyByTemplateCode.size !== SHOP_VERTICAL_TEMPLATE_DESCRIPTORS.length) {
    throw new Error("Shop vertical presentation catalog contains duplicate template codes.");
}

const authoredPresentationFamilyByMarker = new Map<string, ShopVerticalPresentationProfile>([
    ["boutique", "boutique"],
    ["commerce", "commerce"],
    ["editorial", "editorial"],
    ["kids", "kids"],
    ["utility", "utility"],
]);

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function authoredPresentationFamily(value: unknown): ShopVerticalPresentationProfile | undefined {
    if (typeof value !== "string") return undefined;

    return authoredPresentationFamilyByMarker.get(value.trim().toLowerCase());
}

/**
 * Resolves presentation only for the vertical system-template identity.
 * Laguna, Morrow and workspace snapshot codes intentionally stay unprofiled.
 */
export function resolveShopVerticalPresentationFamily(
    templateCode: string | null | undefined,
): ShopVerticalPresentationProfile | undefined {
    const normalizedCode = templateCode?.trim().toLowerCase() ?? "";

    return normalizedCode === ""
        ? undefined
        : presentationFamilyByTemplateCode.get(normalizedCode);
}

/**
 * Recovers the authored family identity when a vertical template has been
 * copied under a workspace-specific code. Only direct page blocks are read so
 * arbitrary nested content cannot accidentally restyle the whole storefront.
 * Conflicting root markers fail closed and leave the renderer unprofiled.
 */
export function resolveAuthoredShopVerticalPresentationFamily(
    blocks: readonly unknown[] | null | undefined,
): ShopVerticalPresentationProfile | undefined {
    const authoredFamilies = new Set<ShopVerticalPresentationProfile>();

    for (const block of blocks ?? []) {
        if (!isRecord(block) || !isRecord(block.options)) continue;

        const profile = authoredPresentationFamily(block.options.shopPresentationProfile);

        if (profile) {
            authoredFamilies.add(profile);
        }
    }

    return authoredFamilies.size === 1
        ? authoredFamilies.values().next().value
        : undefined;
}
